import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService, RefreshTokenService } from '@auto-editor/feature-auth';
import { environment } from '@auto-editor/environment';

@Injectable()
export class RefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private refreshTokenService: RefreshTokenService,
    private authService: AuthService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (this.shouldRefreshProactively(request)) {
      return this.handle401Error(request, next);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 401 && !this.isTokenEndpoint(request)) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const oauthInfo = this.authService.getOauthInfo();

      if (!oauthInfo?.refresh_token) {
        this.isRefreshing = false;
        this.redirectToLogin();
        return throwError(() => new Error('Missing refresh token'));
      }

      return this.refreshTokenService.refreshToken(oauthInfo.refresh_token).pipe(
        switchMap((newOauthInfo) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newOauthInfo.access_token);
          return next.handle(this.addTokenHeader(request, newOauthInfo.access_token));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.redirectToLogin();
          return throwError(() => error);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private shouldRefreshProactively(request: HttpRequest<any>): boolean {
    if (this.isTokenEndpoint(request)) return false;

    const oauthInfo = this.authService.getOauthInfo();
    if (!oauthInfo?.access_token) return false;

    return this.authService.isAccessTokenExpired();
  }

  private isTokenEndpoint(request: HttpRequest<any>): boolean {
    return request.url.includes('oauth/token');
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    });
  }

  private redirectToLogin(): void {
    const urlLogin =
      environment.logIn +
      `?response_type=code&client_id=${environment.apiClientId}&scope=${environment.scope}&redirect_uri=` +
      encodeURIComponent(window.location.origin + '/auth');

    window.location.href = urlLogin;
  }
}
